// NameLogin — learner enters their real name; we match against /users.name
// (whitespace-insensitive) and bind the learner's userId in localStorage.

import {
  BreathDot,
  FogLayer,
  Han,
  InputLine,
  PlatformEdge,
  RailLayer,
  T,
  TextBtn,
} from "@goodbye/ui";
import {
  getFirebase,
  signInAnon,
  usersCol,
  useFirestoreCollection,
} from "@goodbye/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import {
  hasWelcomed,
  normalizeName,
  setLearnerIdentity,
  setCohortId,
} from "../hooks/useNameAuth";

export function NameLogin() {
  // Anonymous sign-in must complete BEFORE the /users snapshot subscribes;
  // Firestore Rules require isAuthed() and the first snapshot otherwise
  // fails silently, leaving users=[] and every name "not found".
  const [authed, setAuthed] = useState(
    () => getFirebase().auth.currentUser !== null,
  );
  useEffect(() => {
    if (authed) return;
    let cancelled = false;
    signInAnon().catch((err) => {
      if (!cancelled && typeof console !== "undefined") {
        console.warn("[NameLogin] signInAnon failed", err);
      }
    });
    const unsub = onAuthStateChanged(getFirebase().auth, (u) => {
      if (u && !cancelled) setAuthed(true);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [authed]);

  const usersQ = useMemo(() => (authed ? usersCol() : null), [authed]);
  const { data: users, loading } = useFirestoreCollection(usersQ);

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stripped = normalizeName(input);

  async function handleEnter() {
    setError(null);
    const target = stripped;
    if (!target) return;
    if (!authed || loading) {
      setError("還在連線,請再試一次。");
      return;
    }
    if (users.length === 0) {
      // /users snapshot returned empty — either Rules denied (auth race) or
      // the collection genuinely has no docs. Don't tell the learner their
      // name is wrong when the real problem is upstream.
      setError("名單還沒載入。請等一下重試;若持續發生請告訴工作人員。");
      return;
    }
    setBusy(true);
    try {
      const match = users.find((u) => normalizeName(u.name) === target);
      if (!match) {
        setError("找不到這個名字。請確認跟報名時填的一樣。");
        setBusy(false);
        return;
      }
      await signInAnon();
      setLearnerIdentity(match.id);
      // Belt-and-suspenders: also write cohortId here so it's available
      // immediately, not only after the reload triggers useNameAuth's useEffect.
      setCohortId(match.cohortId ?? "2025");
      // 2026-05-28: 登入後落地 RootRedirect → 第一次走 /welcome(儀式)、
      // 之後走 /home(主畫面、含聲音/地圖/訊息/碎片入口)。先前直接跳
      // /playlist 讓「聲音」變成預設首頁、跟 Stage 10 加碎片/訊息後的
      // 多 surface 結構不符,所以改回讓 root redirect 決定。
      void hasWelcomed; // keep import referenced; flag still read by RootRedirect
      window.location.replace("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        background: T.paper,
      }}
    >
      <FogLayer opacity={0.9} />
      <RailLayer side="right" opacity={0.5} />
      <PlatformEdge bottom={108} opacity={0.4} />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: "calc(54px + env(safe-area-inset-top, 0px)) 32px 0",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 40,
          }}
        >
          <BreathDot />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Han size={24} color={T.ink900} lh={1.6}>
              再見。再見
            </Han>
            <div
              style={{
                fontFamily: T.mono,
                fontSize: 11,
                letterSpacing: "0.16em",
                color: T.ink500,
                lineHeight: 1.7,
              }}
            >
              THE ECHOES WE FOLLOW<br />
              LEAD US TO OURSELVES.
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* Day 14 #8: error moved ABOVE input so iOS keyboard
                doesn't hide it. aria-live announces the change to
                screen readers. paperSink background keeps the
                grayscale-only design system intact. */}
            <div role="alert" aria-live="polite">
              {error && (
                <div
                  style={{
                    background: T.paperSink,
                    padding: "10px 12px",
                    marginBottom: 4,
                  }}
                >
                  <Han size={12} color={T.ink900}>
                    · {error}
                  </Han>
                </div>
              )}
            </div>
            <Han size={12} color={T.ink500}>你的名字</Han>
            <InputLine
              value={input}
              onChange={setInput}
              placeholder="例:你的本名"
              dir="instrument"
              autoComplete="name"
              inputMode="text"
              ariaLabel="你的真實中文姓名"
              onSubmit={handleEnter}
              autoFocus
            />
            {loading && (
              <Han size={11} color={T.ink350}>· 載入名單中…</Han>
            )}
          </div>
        </div>

        <div
          style={{
            padding: "0 32px calc(32px + env(safe-area-inset-bottom, 0px))",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
          }}
        >
          <TextBtn
            onClick={handleEnter}
            disabled={busy || !stripped || loading}
            color={!busy && stripped && !loading ? T.ink900 : T.ink350}
            mono={false}
            size={16}
            ls="0.3em"
          >
            {busy ? "確 認 中…" : "進 入"}
          </TextBtn>
        </div>
      </div>
    </div>
  );
}
